import { useQuery } from '@tanstack/react-query';
import { API_URL } from 'config';
import { useEffect, useState } from 'react';
import { getToken } from 'utils/get-token/get-token';

/**
 * User lookup for the impersonation picker, backed by the identity API's
 * `listUsers` endpoint.
 *
 * NOTE: `emailOrProposalEmail` is an **exact** (case-insensitive) match on the
 * server, so this resolves a whole e-mail to a user — it is not a substring
 * search. A `contains` filter is proposed in identity-api#16; once deployed
 * this can become search-as-you-type.
 */

const DEBOUNCE_MS = 300;

export type SearchedUser = {
  id: string;
  email?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  isActive?: boolean | null;
};

type ListUsersResponse = {
  items?: SearchedUser[];
};

/** Cheap "could this be a whole e-mail" test — avoids pointless requests. */
const looksLikeEmail = (value: string) => /^\S+@\S+\.\S+$/.test(value.trim());

const fetchUsers = async (email: string) => {
  const response = await fetch(`${API_URL}/users/users`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${getToken()}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      pageIndex: 0,
      pageSize: 20,
      emailOrProposalEmail: email.trim(),
    }),
  });

  if (!response.ok) {
    throw new Error(`listUsers failed with status ${response.status}`);
  }

  const data = (await response.json()) as ListUsersResponse;
  return data.items ?? [];
};

export const useUserSearch = (query: string, enabled: boolean) => {
  const [debouncedQuery, setDebouncedQuery] = useState(query);

  useEffect(() => {
    const timeout = setTimeout(() => setDebouncedQuery(query), DEBOUNCE_MS);
    return () => clearTimeout(timeout);
  }, [query]);

  const isQueryComplete = looksLikeEmail(debouncedQuery);

  const { data, isFetching, isError } = useQuery({
    queryKey: [
      'impersonation-user-lookup',
      debouncedQuery.trim().toLowerCase(),
    ],
    queryFn: () => fetchUsers(debouncedQuery),
    enabled: enabled && isQueryComplete,
    staleTime: 30_000,
  });

  return {
    users: isQueryComplete ? (data ?? []) : [],
    isFetching: isQueryComplete && (isFetching || debouncedQuery !== query),
    isError,
    /** The input is not a whole e-mail yet — nothing was looked up. */
    needsFullEmail: !isQueryComplete,
  };
};
